/**
 * Character Scores — Hidden GM Mechanics
 *
 * Each PC has a unique hidden score tracked by the GM that subtly affects gameplay.
 * Players see vague labels but not the internal mechanics.
 *
 * - Skolnit: Reputation (probabilistic social bonus)
 * - Mad Juju: Mojo (attack cap/bonus)
 * - Dax: Inventor's Inspiration (threshold gate for inventions)
 * - Bobby: Echo Disturbance (see echo-madness.ts)
 */
export const PERSONAL_REPUTATION_TIERS = [
    { name: 'Unknown', playerLabel: 'Unknown', minScore: 0, color: '#6b7280', chance: 0, socialBonus: 0 },
    { name: 'Noticed', playerLabel: 'Getting noticed', minScore: 2, color: '#a3a3a3', chance: 20, socialBonus: 1 },
    { name: 'Known', playerLabel: 'Word spreads', minScore: 4, color: '#fbbf24', chance: 35, socialBonus: 2 },
    { name: 'Notable', playerLabel: 'Notable figure', minScore: 6, color: '#f59e0b', chance: 50, socialBonus: 2 },
    { name: 'Famous', playerLabel: 'Famous', minScore: 8, color: '#d4af37', chance: 65, socialBonus: 3 },
    { name: 'Legendary', playerLabel: 'Legendary', minScore: 10, color: '#fcd34d', chance: 80, socialBonus: 3 },
];
export function getPersonalReputationTier(score) {
    for (let i = PERSONAL_REPUTATION_TIERS.length - 1; i >= 0; i--) {
        if (score >= PERSONAL_REPUTATION_TIERS[i].minScore)
            return PERSONAL_REPUTATION_TIERS[i];
    }
    return PERSONAL_REPUTATION_TIERS[0];
}
/**
 * Returns the bonus and chance for a reputation score.
 * Caller should do `Math.random() * 100 < chance` to decide if bonus fires.
 */
export function applyReputationBonus(score) {
    const tier = getPersonalReputationTier(score);
    return { bonus: tier.socialBonus, chance: tier.chance };
}
/** Skills that reputation affects */
export const SOCIAL_SKILLS = ['Persuasion', 'Intimidation', 'Deception', 'Performance'];
export const MOJO_TIERS = [
    { name: 'Empty', playerLabel: 'Mojo gone', minScore: 0, color: '#6b7280' },
    { name: 'Drained', playerLabel: 'Mojo flickering', minScore: 2, color: '#a78bfa' },
    { name: 'Steady', playerLabel: 'Mojo steady', minScore: 4, color: '#818cf8' },
    { name: 'Surging', playerLabel: 'Mojo rising', minScore: 6, color: '#6366f1' },
    { name: 'Overflowing', playerLabel: 'Mojo strong', minScore: 8, color: '#4f46e5' },
    { name: 'Unleashed', playerLabel: 'Mojo unleashed', minScore: 10, color: '#7c3aed' },
];
export function getMojoTier(score) {
    for (let i = MOJO_TIERS.length - 1; i >= 0; i--) {
        if (score >= MOJO_TIERS[i].minScore)
            return MOJO_TIERS[i];
    }
    return MOJO_TIERS[0];
}
/**
 * Returns the mojo effect for a given score.
 * - cap: if non-null, d20 die result is clamped to this value (before modifiers)
 * - attackBonus: extra bonus added to attack rolls
 */
export function getMojoEffect(score) {
    if (score <= 1)
        return { cap: 15, attackBonus: 0 };
    if (score <= 3)
        return { cap: 17, attackBonus: 0 };
    if (score <= 5)
        return { cap: null, attackBonus: 0 };
    if (score <= 7)
        return { cap: null, attackBonus: 1 };
    if (score <= 9)
        return { cap: null, attackBonus: 2 };
    return { cap: null, attackBonus: 3 };
}
export const INVENTOR_TIERS = [
    { name: 'Dormant', playerLabel: 'Tinkering', minScore: 0, color: '#6b7280' },
    { name: 'Sparking', playerLabel: 'Ideas forming', minScore: 2, color: '#f59e0b' },
    { name: 'Almost', playerLabel: 'On the verge', minScore: 4, color: '#f97316' },
    { name: 'Ready', playerLabel: 'Eureka!', minScore: 5, color: '#22c55e' },
];
export const INVENTOR_THRESHOLD = 5;
export function getInventorTier(score) {
    for (let i = INVENTOR_TIERS.length - 1; i >= 0; i--) {
        if (score >= INVENTOR_TIERS[i].minScore)
            return INVENTOR_TIERS[i];
    }
    return INVENTOR_TIERS[0];
}
//# sourceMappingURL=character-scores.js.map